import BinocularsIcon from '@components/BinocularsIcon';
import { BidInfo } from '@components/cars-table-next/legacy/BidInfo';
import ImageOverlay from '@components/ui/ImageOverlay';
import { Badge, createStyles, makeStyles, SvgIconTypeMap, Tooltip, withStyles } from '@material-ui/core';
import { yellow } from '@material-ui/core/colors';
import { DefaultComponentProps } from '@material-ui/core/OverridableComponent';
import { MoneyOff, Star } from '@material-ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { transparentize } from 'polished';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKeys } from 'src/constants';
import useApi from 'src/hooks/useApi';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import ApiService from 'src/modules/api-service';

import { BidSuccessStatus, IndividualBidDTO, IndividualBidStateInfoDTO } from '../../../modules/generated/api';

type CarImageProps = {
  bidStateInfo?: IndividualBidStateInfoDTO;
  imageThumbnailLink?: string;
  expired?: boolean;
  vin?: string;
  watchlist?: string[];
};

const useStyles = makeStyles((theme) => ({
  backgroundBlackAndWhite: {
    filter: 'grayscale(100%)',
  },
  iconStyle: {
    backgroundColor: transparentize(0.2, theme.palette.error.main),
    borderRadius: 90,
    color: '#fff',
    border: '1px solid white',
  },
  watchlistIcon: {
    backgroundColor: theme.palette.common.white,
    paddingRight: '2px',
    borderRadius: '50%',
  },
  watchlistIconInactive: {
    color: theme.palette.secondary.light,
  },
  watchlistIconActive: {
    color: theme.palette.watchlistIconActive,
  },
}));

const HighestBidBadge = withStyles(() =>
  createStyles({
    badge: {
      background: yellow[50],
      color: yellow[900],
      padding: 0,
    },
  }),
)(Badge);

// naming components for easier code-reading
const WatchlistBadge = Badge;
const HammerBadge = Badge;

const getIsHighestBidder = (bids: IndividualBidDTO[]) => bids.some((bid) => bid.status === BidSuccessStatus.Highest);

export const CarImage = ({ expired, bidStateInfo, imageThumbnailLink, vin, watchlist }: CarImageProps) => {
  const classes = useStyles();
  const { fetch } = useApi();
  const { t } = useTranslation();
  const snackbar = useCustomSnackbarGlobal();
  const queryClient = useQueryClient();
  const { customerModeActive } = useCustomerMode();
  const isHighestBidder = bidStateInfo?.ownCurrentBids && getIsHighestBidder(bidStateInfo.ownCurrentBids);
  // check cars is in the url
  const isCarsTable = window.location.pathname.includes('cars');
  const [isOnWatchlist, setOnWatchlist] = useState<boolean>(false);

  useEffect(() => {
    setOnWatchlist(!isEmpty(watchlist));
  }, [watchlist]);

  const addToDefaultWatchlist = async () => {
    if (!vin) return;
    const response = await fetch(ApiService.watchListController.watchListControllerAddCarToDefaultWatchList({ vin }));
    if (response && response.status === 200) {
      snackbar.showSuccess(t('snackBar.success.addedToWatchlist'));
      setOnWatchlist(true);
    } else {
      snackbar.showError(t('snackBar.error.addedToWatchlist'));
    }
    // invalidate query key to reload data
    queryClient.invalidateQueries(isCarsTable ? [QueryKeys.carsList] : [QueryKeys.bidsList]);
  };

  const removeFromDefaultWatchlist = async () => {
    if (!vin) return;
    const response = await fetch(
      ApiService.watchListController.watchListControllerRemoveCarFromDefaultWatchList({ vin }),
    );
    if (response && response.status === 204) {
      snackbar.showSuccess(t('snackBar.success.removedFromWatchlist'));
      setOnWatchlist(false);
    } else {
      snackbar.showError(t('snackBar.error.removeFromWatchlist'));
    }
    // invalidate query key to reload data
    queryClient.invalidateQueries(isCarsTable ? [QueryKeys.carsList] : [QueryKeys.bidsList]);
  };

  return (
    <HighestBidBadge
      badgeContent={
        isHighestBidder && !customerModeActive ? (
          <Tooltip title={t('bids.status.highest')} arrow placement="right">
            <Star fontSize="small" color="inherit" />
          </Tooltip>
        ) : null
      }
      overlap="rectangular"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <HammerBadge
        badgeContent={isCarsTable && bidStateInfo && !customerModeActive ? <BidInfo bidStatus={bidStateInfo} /> : null}
        overlap="rectangular"
      >
        <WatchlistBadge
          key={`${vin}-watchlist-icon`}
          badgeContent={
            isOnWatchlist ? (
              <BinocularsIcon
                key={`${vin}-active`}
                className={clsx(classes.watchlistIconActive, classes.watchlistIcon)}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFromDefaultWatchlist();
                }}
              />
            ) : (
              <BinocularsIcon
                key={`${vin}-inactive`}
                className={clsx(classes.watchlistIconInactive, classes.watchlistIcon)}
                onClick={(e) => {
                  e.stopPropagation();
                  addToDefaultWatchlist();
                }}
              />
            )
          }
          overlap="rectangular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <ImageOverlay
            src={imageThumbnailLink ?? '/images/icons/car.svg'}
            icon={(iconProps: DefaultComponentProps<SvgIconTypeMap>) => (
              <MoneyOff fontSize="medium" className={classes.iconStyle} {...iconProps} />
            )}
            height={40}
            tooltipText={t('bids.expired')}
            disableOverlay={!isCarsTable || !expired}
          />
        </WatchlistBadge>
      </HammerBadge>
    </HighestBidBadge>
  );
};
